import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { DataTable } from '../../components/tables/DataTable';
import { ROUTES } from '../../constants/routes';
import { getSiteLocations } from '../../services/siteService';
import { deleteLocation } from '../../services/locationService';
import type { Site, SiteLocationRow } from '../../types/project';
import { siteLocationsColumns } from './sitesColumns';
import { EditSiteDialog } from './EditSiteDialog';
import { EditLocationDialog } from './EditLocationDialog';
import { DeleteLocationDialog } from './DeleteLocationDialog';
import { NewLocationForSiteDialog } from './NewLocationForSiteDialog';
import { usePermissions } from '../../hooks/usePermissions';

export const SiteDetailPage = () => {
  const { siteKey } = useParams<{ siteKey: string }>();
  const navigate = useNavigate();
  const { canManageEntities } = usePermissions();
  const [site, setSite] = useState<Site | null>(null);
  const [locations, setLocations] = useState<SiteLocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('ProjectName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editSiteOpen, setEditSiteOpen] = useState(false);
  const [newLocationOpen, setNewLocationOpen] = useState(false);
  const [editLocation, setEditLocation] = useState<SiteLocationRow | null>(null);
  const [deleteLocationState, setDeleteLocationState] = useState<SiteLocationRow | null>(null);

  const fetchData = useCallback(async () => {
    if (!siteKey) return;
    setLoading(true);
    try {
      const result = await getSiteLocations(siteKey);
      setSite(result.site);
      setLocations(result.locations);
    } catch {
      setSite(null);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [siteKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleEditLocation = (row: Record<string, unknown>) => {
    const loc = locations.find((l) => l.LocationKey === row.LocationKey) ?? null;
    setEditLocation(loc);
  };

  const handleDeleteLocation = (row: Record<string, unknown>) => {
    const loc = locations.find((l) => l.LocationKey === row.LocationKey) ?? null;
    setDeleteLocationState(loc);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteLocationState) return;
    await deleteLocation(deleteLocationState.LocationKey);
    setDeleteLocationState(null);
    fetchData();
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    const projectId = row.ProjectId as string;
    navigate(ROUTES.LOCATIONS.replace(':projectId', projectId));
  };

  const sortedLocations = [...locations].sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[sortBy];
    const bVal = (b as unknown as Record<string, unknown>)[sortBy];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const breadcrumbs = [
    { label: 'Sites', path: ROUTES.SITES, entityType: 'site' as const },
    { label: site?.SiteName ?? 'Loading...', entityType: 'site' as const },
  ];

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      <Box sx={{ mb: 2 }}><Breadcrumb items={breadcrumbs} /></Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate(ROUTES.SITES)}
          sx={{ textTransform: 'none', mr: 1 }}>
          Back
        </Button>
        <Typography variant="h4" sx={{
          fontWeight: 800, color: 'text.primary', letterSpacing: '-0.025em',
          fontSize: '1.75rem', lineHeight: 1.2,
        }}>
          {site?.SiteName ?? ''}
        </Typography>
        {site && (
          <Chip label={site.SiteCity} size="small" sx={{ fontWeight: 500 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {canManageEntities && (
          <Button size="small" variant="outlined" onClick={() => setEditSiteOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}>
            Edit Site
          </Button>
        )}
        {canManageEntities && (
          <Button size="small" variant="contained" startIcon={<Add />}
            onClick={() => setNewLocationOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '10px',
              px: 2.5, py: 0.75, fontSize: '0.8125rem',
            }}>
            Add Location
          </Button>
        )}
      </Box>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {locations.length} location{locations.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Locations table */}
      <DataTable
        columns={siteLocationsColumns}
        data={sortedLocations as unknown as Record<string, unknown>[]}
        loading={loading}
        onRowClick={handleRowClick}
        onEditClick={canManageEntities ? handleEditLocation : undefined}
        onDeleteClick={canManageEntities ? handleDeleteLocation : undefined}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      <NewLocationForSiteDialog
        open={newLocationOpen}
        siteKey={siteKey ?? ''}
        onClose={() => setNewLocationOpen(false)}
        onCreated={() => { setNewLocationOpen(false); fetchData(); }}
      />
      <EditSiteDialog
        open={editSiteOpen}
        site={site}
        onClose={() => setEditSiteOpen(false)}
        onUpdated={() => { setEditSiteOpen(false); fetchData(); }}
      />
      <EditLocationDialog
        open={!!editLocation}
        location={editLocation}
        siteKey={siteKey ?? ''}
        onClose={() => setEditLocation(null)}
        onUpdated={() => { setEditLocation(null); fetchData(); }}
      />
      <DeleteLocationDialog
        open={!!deleteLocationState}
        location={deleteLocationState}
        onClose={() => setDeleteLocationState(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default SiteDetailPage;
